import { useState } from 'react'
import { ApiError, apiFetch } from '@/lib/apiClient'
import { env } from '@/lib/env'
import { setAuthItem } from '@/lib/authStorage'
import { SELECTED_CONTAINER_KEY } from '@/lib/api/workspace-context'
import type { PendingInvite } from '@/types/session'

interface TeamJoinGateProps {
  invite: PendingInvite
  authToken: string | null
  onError: (msg: string | null) => void
}

const MAX_WORKSPACE_NAME_LEN = 28

function truncate(name: string | null | undefined): string {
  if (!name) return ''
  if (name.length <= MAX_WORKSPACE_NAME_LEN) return name
  return `${name.slice(0, MAX_WORKSPACE_NAME_LEN - 1)}…`
}

interface AcceptAgreementsResponse {
  success?: boolean
  accessToken?: string
  error?: string
}

interface AcceptInviteResponse {
  success?: boolean
  membership?: unknown
  spaceId?: string
  role?: string
  error?: string
}

/**
 * Team-workspace welcome gate (#1776).
 *
 * Renders for any user with one or more pending team invites. The CTA atomically:
 *   1. POST /auth/accept-agreements — records ToS+Privacy at the user level
 *      (idempotent server-side; safe even if already accepted).
 *   2. POST /workspaces/invites/:id/accept — creates the workspace membership.
 *   3. window.location.reload() — SessionProvider re-fetches and routes the
 *      user into the team workspace.
 *
 * Per-membership `team_members.agreements_accepted_at` (per the spec's
 * recommendation) is intentionally NOT introduced here — the simpler
 * user-level flag is reused. Migrate to per-membership later if/when team
 * owners need an audit trail of "who agreed when".
 *
 * Multiple pending invites: PlanGate passes the OLDEST pending invite first
 * (sorted by invitedAt). After accepting one, the gate re-renders for the
 * next pending invite, until none remain — matching the spec's
 * "one gate per team, in order of invite timestamp" decision.
 */
export function TeamJoinGate({ invite, authToken, onError }: TeamJoinGateProps) {
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const workspaceNameRaw = invite.containerName ?? null
  const workspaceName = truncate(workspaceNameRaw)
  const ownerEmail = invite.ownerEmail

  function setStorageToken(token: string | undefined) {
    if (!token) return
    // Persist via setAuthItem so the rotated (post-join) token lands in the
    // platform-canonical store (localStorage on native, sessionStorage on web).
    // A raw sessionStorage write was read back as stale on native after the
    // reload below, since the reader now uses localStorage.
    setAuthItem('xavior_auth_token', token)
  }

  async function handleSubmit() {
    if (!authToken) {
      onError('Your session expired—please log in again to continue.')
      return
    }
    if (!agreedToTerms) {
      onError('Please agree to the Terms of Service and Privacy Policy to continue.')
      return
    }

    setSubmitting(true)
    onError(null)

    try {
      // 1. Record terms acceptance (idempotent at user level).
      const ack = await apiFetch<AcceptAgreementsResponse>(
        'auth/accept-agreements',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          authToken,
        },
      )
      setStorageToken(ack?.accessToken)

      // 2. Accept the invite — creates the membership and dismisses the invite.
      // #3305: consolidated onto the OpenAPI /workspaces/* surface.
      const accepted = await apiFetch<AcceptInviteResponse>(
        `workspaces/invites/${invite.id}/accept`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          authToken: ack?.accessToken ?? authToken,
        },
      )

      // Pin the joined workspace as the active container BEFORE reload. Without
      // this, SessionProvider's default selection falls back to `firstOwned` —
      // the invitee's own personal workspace — instead of the team they just
      // joined. The accept response returns the joined workspace id (spaceId).
      const joinedId = accepted?.spaceId
      if (joinedId) {
        try {
          setAuthItem(SELECTED_CONTAINER_KEY, JSON.stringify({ id: joinedId, ownerId: invite.ownerId }))
        } catch { /* best-effort — falls back to default selection */ }
      }

      // 3. Reload — SessionProvider re-fetches containers + invites + profile,
      //    and (via the pin above) routes into the team workspace just joined.
      window.location.reload()
    } catch (err) {
      const fallback = "Couldn't join the team. Please try again."
      if (err instanceof ApiError) {
        const payload = err.payload as { error?: string } | undefined
        onError(payload?.error ?? fallback)
      } else {
        onError(fallback)
      }
      setSubmitting(false)
    }
  }

  return (
    <div className="w-full max-w-md text-center">
      <div className="mb-6 inline-flex items-center justify-center">
        <img src="/favicon.svg" alt="XAVIOR" className="h-14 w-14" />
      </div>

      <h3 className="text-2xl font-bold leading-tight tracking-tight text-gray-900 md:text-[32px]">
        {workspaceName ? (
          <>
            You&rsquo;re on team{' '}
            <span className="bg-gradient-to-b from-transparent from-[62%] via-sky-100 via-[62%] to-sky-100 to-[88%]">
              &ldquo;{workspaceName}&rdquo;
            </span>
          </>
        ) : (
          <>You&rsquo;re on your new team</>
        )}
      </h3>

      <p className="mx-auto mt-3 max-w-sm text-[15px] leading-relaxed text-gray-600">
        Your account is paid for by the team owner,{' '}
        <strong className="font-medium text-gray-900">{ownerEmail}</strong>. You can add your
        own workspace anytime.
      </p>

      <label className="mt-8 mb-4 flex items-start justify-center gap-3 text-left">
        <input
          type="checkbox"
          checked={agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
        />
        <span className="text-sm text-gray-600">
          I agree to the{' '}
          <a
            href={env.termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-600 hover:underline"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href={env.privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-600 hover:underline"
          >
            Privacy Policy
          </a>
        </span>
      </label>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || !agreedToTerms}
        className="w-full rounded-lg bg-sky-500 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? 'Joining team…' : 'Accept terms and continue'}
      </button>

      <p className="mt-4 text-xs text-gray-500">
        Team seat. Billed to your workspace owner.{' '}
        <a href="mailto:support@xavior.ai" className="text-sky-600 hover:underline">
          Questions?
        </a>
      </p>
    </div>
  )
}
